/**
 * FleetTrash — Corbeille parc auto (véhicules, contrats, coûts, sinistres) avec restore/purge.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trash2, ArchiveRestore, Trash, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function FleetTrash() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("fleet.write") || hasPermission("admin");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/fleet/trash"); setRows(r.data.trash || []); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (col, id) => {
    try { await api.post(`/fleet/trash/${col}/${id}/restore`); toast.success("Restauré"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const purge = async (col, id) => {
    if (!window.confirm("Suppression DÉFINITIVE — action irréversible. Confirmer ?")) return;
    try { await api.delete(`/fleet/trash/${col}/${id}/purge`); toast.success("Purgé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const purgeOld = async () => {
    if (!window.confirm("Purger tous les items supprimés depuis plus de 30 jours (définitif) ?")) return;
    try { const r = await api.post("/fleet/trash/purge-old"); toast.success(`${r.data.purged} items purgés`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const willPurge = rows.filter(r => r._will_purge).length;

  return (
    <div className="space-y-6" data-testid="fleet-trash-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Corbeille parc auto
          </h1>
          <p className="text-sm text-slate-500">
            {rows.length} item{rows.length > 1 ? "s" : ""} · rétention 30 j
            {willPurge > 0 && (
              <> · <span className="text-red-600 font-medium">{willPurge} en purge imminente</span></>
            )}
          </p>
        </div>
        {canWrite && rows.length > 0 && (
          <Button variant="outline" onClick={purgeOld} className="text-red-600 border-red-200" data-testid="fleet-purge-old-btn">
            <Trash className="w-4 h-4 mr-1.5" /> Purger &gt; 30 j
          </Button>
        )}
      </div>

      <Card><CardContent className="p-0">
        {loading ? (
          <div className="p-12 text-center text-slate-500">Chargement...</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center">
            <Trash2 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">Corbeille vide 🎉</p>
          </div>
        ) : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase">Type</TableHead>
              <TableHead className="text-xs uppercase">Description</TableHead>
              <TableHead className="text-xs uppercase">Supprimé le</TableHead>
              <TableHead className="text-xs uppercase">Rétention</TableHead>
              <TableHead className="text-xs uppercase text-right"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={`${r.collection}-${r.id}`} className="border-b hover:bg-slate-50"
                  data-testid={`fleet-trash-row-${i}`}>
                  <TableCell><Badge variant="outline">{r.type_label}</Badge></TableCell>
                  <TableCell className="text-sm">{r.description}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">
                    {r.deleted_at ? new Date(r.deleted_at).toLocaleDateString("fr-FR") : "—"}
                  </TableCell>
                  <TableCell>
                    {r._will_purge ? (
                      <Badge variant="outline" className="bg-red-100 text-red-700 border-red-200">
                        <AlertTriangle className="w-3 h-3 mr-1" />Purge imminente
                      </Badge>
                    ) : (
                      <span className="text-xs text-slate-500 font-mono">J-{Math.max(0, 30 - (r.days_since || 0))}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    {canWrite && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => restore(r.collection, r.id)}
                          className="text-emerald-700 h-7" data-testid={`fleet-restore-${i}`}>
                          <ArchiveRestore className="w-3.5 h-3.5 mr-1" /> Restaurer
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => purge(r.collection, r.id)}
                          className="text-red-600 h-7" data-testid={`fleet-purge-${i}`}>
                          <Trash className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
    </div>
  );
}
